import { StyledCol, StyledInput, StyledRow, StyledSelect, StyledTextArea } from '@/shared/styles';
import { StyledLabel } from '../Forms.styles';
import { Form } from 'antd';
import { GradientBorderButton } from '@/shared/components/Button/GradientBorderButton';
import { U1Button } from '@/shared/components/Button/U1Button';
import { useTranslation } from 'react-i18next';
import { DatepickerSingleInput } from '../../Datepicker/DatepickerSingleInput';
import { EnterpriseInnerFormProps } from './EnterpriseForm.consts';
import { formatDateFromTimestamp } from '@/shared/utils/datepicker';
import { useFrameworkStore } from '@/stores/ProjectStore/ProjectStore';
import { useEffect, useState } from 'react';
import { CreateEnterpriseFrameworkDto } from '@/stores/EnterpriseStore/EnterpriseStore.consts';
import { FRAMEWORKS_ENUM } from '@/shared/consts';
import { getFieldsSettings } from '../Forms.consts';
import { RequiredFieldGroup } from '../../Framework/RequiredFieldGroup';

export const EnterpriseInnerForm: React.FC<EnterpriseInnerFormProps> = ({
  selected,
  onValuesChange,
  onCancel,
  submitBtnText,
}: EnterpriseInnerFormProps) => {
  const { t } = useTranslation();
  const [selectedFrameworks, setSelectedFrameworks] = useState<CreateEnterpriseFrameworkDto[]>([]);

  const handleFrameworksChange = (frameworks: string[]) => {
    const result: CreateEnterpriseFrameworkDto[] = [];

    for (const framework of frameworks) {
      const exists = selectedFrameworks.find((fw) => fw.code === framework);

      if (exists) {
        result.push(exists);
      } else {
        result.push({ code: framework as FRAMEWORKS_ENUM });
      }
    }

    setSelectedFrameworks(result);
  };

  const frameworkStore = useFrameworkStore();

  const onSingleDateSelect = (date: string) => {
    onValuesChange({ expiresAt: new Date(date).toISOString() });
  };

  useEffect(() => {
    frameworkStore.getFrameworksData();
  }, []);

  useEffect(() => {
    if (selected) {
      setSelectedFrameworks(
        selected.frameworks.map((fw) => ({
          ...fw,
          code: fw.code as FRAMEWORKS_ENUM,
        }))
      );
    }
  }, [selected]);

  return (
    <>
      <StyledCol $justify="center" $width="100%" $gap={8}>
        <StyledLabel>{t('admin.table.name')}*</StyledLabel>
        <Form.Item
          name="name"
          rules={[
            {
              required: true,
              message: `${t('admin.table.name')} ${t('form.required')}`,
            },
          ]}
        >
          <StyledInput placeholder={t('admin.table.name')} />
        </Form.Item>

        <StyledLabel>{t('admin.table.description')}</StyledLabel>
        <Form.Item name="description">
          <StyledTextArea placeholder={t('admin.table.description')} />
        </Form.Item>

        <StyledLabel>
          {t('admin.table.emails')} (email@gmail.com, email1@gmail.com, ...)
        </StyledLabel>
        <Form.Item
          name="emails"
          rules={[
            {
              required: true,
              message: `${t('admin.table.emails')} ${t('form.required')}`,
            },
          ]}
        >
          <StyledInput placeholder={t('admin.table.emails')} />
        </Form.Item>

        <StyledLabel>{t('admin.table.frameworks')}</StyledLabel>
        <Form.Item name="frameworks">
          <StyledSelect
            mode="tags"
            placeholder={t('admin.form.select')}
            tokenSeparators={[',']}
            options={frameworkStore.data.map((fw) => ({
              label: fw.title,
              value: fw.code,
            }))}
            onChange={(value) => handleFrameworksChange(value as string[])}
            value={selectedFrameworks.map((fw) => fw.code)}
          />
        </Form.Item>

        {selectedFrameworks.map((framework) => {
          const frameworkData = frameworkStore.data.find((f) => f.code === framework.code);
          const requiredFields = getFieldsSettings(frameworkData?.requiredFields);

          return (
            <RequiredFieldGroup
              key={framework.code}
              frameworkCode={framework.code}
              frameworkTitle={frameworkData?.title ?? t('admin.form.unknown')}
              fields={requiredFields}
            />
          );
        })}

        <StyledLabel>{t('admin.table.expires_at')}</StyledLabel>
        <Form.Item name="expiresAt">
          <DatepickerSingleInput
            presetValue={
              selected?.expiresAt ? formatDateFromTimestamp(selected?.expiresAt) : undefined
            }
            onSelect={onSingleDateSelect}
          />
        </Form.Item>
      </StyledCol>

      <StyledRow $justify="space-between" $width="100%" $gap={24}>
        <Form.Item>
          <GradientBorderButton
            text={t('cancel')}
            $height="50px"
            $width="100%"
            onClick={(e: Event) => onCancel(e)}
          />
        </Form.Item>
        <Form.Item>
          <U1Button text={submitBtnText ?? t('save_changes')} htmlType="submit" />
        </Form.Item>
      </StyledRow>
    </>
  );
};
